import React, { useEffect } from 'react';
import { cloneDeep } from 'lodash';
import {
  makeStyles, TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LazyLoad from 'react-lazyload';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { fetchStaticToken } from '../../helpers/authentication';
import api from '../../config/api';
import TherapistProfilePicture from './TherapistProfilePicture';
import { isLoadingTherapistServicesState } from '../../state/apiRequestStatus';
import { selectedTherapistState, selectedUITherapistState } from '../../state/selected';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
  },
  therapistMugshotWrapper: {
    display: 'inline-block',
    borderRadius: '50%',
    overflow: 'hidden',
    width: '40px',
    height: '40px',
    marginRight: '10px',
  },
  therapistMugshot: {
    display: 'block',
    maxWidth: '100%',
  },
}));

const SelectSpecialist = (props) => {
  const { therapists } = props;
  const classes = useStyles();
  const intl = useIntl();
  const searchPlaceholderText = intl.formatMessage({ id: 'Home.searchInputPlaceholder' });

  const setSelectedTherapist = useSetRecoilState(selectedTherapistState);
  const selectedUITherapist = useRecoilValue(selectedUITherapistState);
  const [
    isLoadingTherapistServices, setIsLoadingTherapistServices,
  ] = useRecoilState(isLoadingTherapistServicesState);

  const selectTherapist = async (t) => {
    const returnedTherapist = cloneDeep(t);
    if (returnedTherapist === null) {
      return setSelectedTherapist(null);
    }

    if (window && window.gtag) {
      window.gtag('event', 'search', {
        search_term: returnedTherapist.name,
        search_type: 'Professional',
        search_lang: intl.locale,
      });
    }

    setIsLoadingTherapistServices(true);

    const responseTherapistServices = await fetchStaticToken({
      url: api.therapistServices,
      method: 'POST',
      body: { therapistID: returnedTherapist.therapistId },
    });

    if (responseTherapistServices.status === 200) {
      returnedTherapist.services = await responseTherapistServices.json();
      setSelectedTherapist(returnedTherapist);
    }

    return setIsLoadingTherapistServices(false);
  };

  const onChangeTherapist = async (event, value) => {
    selectTherapist(value);
  };

  useEffect(() => {
    selectTherapist(selectedUITherapist);
  }, [selectedUITherapist]);

  useEffect(() => {
    selectTherapist(selectedUITherapist);
  }, []);

  return (
    <div className={classes.container}>
      <Autocomplete
        noOptionsText={<FormattedMessage id="SelectSpecialist.noOptions" />}
        disabled={isLoadingTherapistServices}
        options={therapists}
        autoHighlight
        value={selectedUITherapist}
        getOptionSelected={(option, value) => option.name === value.name}
        getOptionLabel={(option) => option.name}
        renderOption={(option) => (
          <>
            <span className={classes.therapistMugshotWrapper}>
              <LazyLoad once scrollContainer=".MuiAutocomplete-listbox">
                <TherapistProfilePicture therapist={option} />
              </LazyLoad>
            </span>
            {option.firstName}
            {' '}
            {option.therapistName}
          </>
        )}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label={searchPlaceholderText}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password', // disable autocomplete and autofill
            }}
          />
        )}
        onChange={onChangeTherapist}
      />
    </div>
  );
};

SelectSpecialist.propTypes = {
  therapists: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default SelectSpecialist;
